<template>
    <div>
        <Paper class="profile-module-root">
            <div v-if="loading()">
                <ProfileModuleSkeleton />
            </div>
            <div class="content p-2" v-else-if="isAuthenticated">
                <div
                    v-if="!dense"
                    id="profile-info"
                    class="py-2 px-3 clickable"
                    @click="toSpotlight"
                    v-b-tooltip.hover.bottom
                    :title="$t('components.smart.profileModule.toProfileHint')"
                >
                    <Avatar :image="profileImage">
                        {{ firstLetterInName }}
                    </Avatar>

                    <h6
                        style="grid-area: name; align-self: end; font-size: 18px; line-height: 26px;"
                        class="nowrap"
                    >
                        {{ name ? name : email }}
                    </h6>
                    <p
                        v-if="name"
                        style="grid-area: email; align-self: start; font-size: 12px"
                        class="text-muted nowrap"
                    >
                        {{ email }}
                    </p>
                </div>
                <div class="p-2" v-if="!dense">
                    <Divider />
                </div>

                <template v-if="score != 100">
                    <div id="spotlight-overview" class="p-2">
                        <h6
                            style="justify-self: start; width: min-content; min-width: 100px"
                        >
                            {{
                                $t(
                                    'components.smart.profileModule.profileCompletion'
                                )
                            }}
                        </h6>
                        <h6
                            style="justify-self: end; font-size: 40px;"
                            class="font-weight-light"
                        >
                            {{ score }}%
                        </h6>
                        <b-progress
                            id="progress-secondary"
                            :value="score"
                            style="grid-column: span 2"
                            height="7px"
                        />
                        <p
                            style="grid-column: span 2; margin: 10px 0; font-size: 14px;"
                            class="subtitle text-muted"
                        >
                            {{
                                $t(
                                    'components.smart.profileModule.profileCompletionEncouragement',
                                    { percentageLeft: 100 - score + '%' }
                                )
                            }}
                        </p>
                    </div>

                    <b-list-group id="spotlight-actions" flush class="mx-2">
                        <b-list-group-item
                            v-for="(action, index) in actions.slice(
                                0,
                                this.limit
                            )"
                            :key="index"
                            button
                            @click="
                                event => {
                                    action.action(event);
                                    $emit('click');
                                }
                            "
                            class="spotlight-action px-0"
                        >
                            <p class="mb-0">{{ action.desc }}</p>
                            <div style="flex-grow: 1"></div>
                            <p class="mb-0">+{{ action.score }}%</p>
                        </b-list-group-item>
                    </b-list-group>

                    <div class="mx-2" v-if="!dense">
                        <Divider />
                    </div>

                    <div class="mx-2 my-3" v-if="!dense">
                        <BaseButton
                            class="w-100"
                            variant="secondary"
                            @click="toSpotlight"
                        >
                            {{
                                $t(
                                    'components.smart.profileModule.gotoProfilePage'
                                )
                            }}
                        </BaseButton>
                    </div>
                </template>
                <template v-else>
                    <div class="p-2">
                        <h6>
                            {{
                                $t(
                                    'components.smart.profileModule.profileCompleted'
                                )
                            }}
                        </h6>
                        <p
                            style="margin: 10px 0; font-size: 14px;"
                            class="subtitle text-muted"
                        >
                            {{
                                $t(
                                    'components.smart.profileModule.profileCompletedWhatsNext'
                                )
                            }}
                        </p>
                    </div>

                    <div class="mx-2">
                        <Divider />
                    </div>

                    <div class="mx-2 my-3">
                        <BaseButton
                            class="w-100"
                            variant="secondary"
                            @click="toTests"
                        >
                            {{
                                $t(
                                    'components.smart.profileModule.gotoTestsPage'
                                )
                            }}
                        </BaseButton>
                    </div>
                </template>
            </div>
            <div v-else>
                <AuthIndication :dense="dense" @click="$emit('click')" />
            </div>
        </Paper>
        <TermsAndConds style="margin-top:var(--grid-gap);" />
    </div>
</template>

<script>
// Component specific imports
import AuthIndication from './components/AuthIndication';
import ProfileModuleSkeleton from '~/components/skeletons/ProfileModuleSkeleton';
import TermsAndConds from '~/shared/TermsAndConds';

// Utils import
import Token from '~/api/Token';
import mapProfileToActions, {
    maxPossibleScore,
} from '~/utils/mappers/mapProfileToActions';

export default {
    name: 'ProfileModule',
    components: { AuthIndication, ProfileModuleSkeleton, TermsAndConds },
    props: {
        dense: Boolean,
        limit: {
            type: Number,
            default: 100,
        },
    },
    computed: {
        profile() {
            return this.$interfaces.user.me.get({ reactive: true });
        },
        candidate() {
            return this.$interfaces.candidate.me.get({ reactive: true });
        },
        actions() {
            // if (i18n.locale) {
            // }
            return this.data() && this.candidate.data
                ? mapProfileToActions(this.data(), this.candidate.data)
                : [];
        },
        maxScore() {
            return maxPossibleScore;
        },
        sumOfGivenScores() {
            return this.actions.reduce((acc, val) => acc + val.score, 0);
        },
        score() {
            return this.maxScore - this.sumOfGivenScores;
        },
        name() {
            return this.data().firstName && this.data().lastName
                ? (this.data().firstName + ' ' || '') +
                      (this.data().lastName || '')
                : this.data().firstName || this.data().lastName || '';
        },
        email() {
            return this.data().email || '';
        },
        profileImage() {
            return this.data().image;
        },
        firstLetterInName() {
            return this.data().firstLetterInName;
        },
        isAuthenticated() {
            return this.$interfaces.logInModule.isLoggedIn();
        },
    },
    methods: {
        data() {
            return (this.profile || {}).data;
        },
        loading() {
            return this.profile.loading;
        },
        error() {
            return this.profile.error;
        },
        toSpotlight() {
            this.$router.push(this.$URLS.spotlight);
        },
        toTests() {
            this.$router.push(this.$URLS.tests.concat('/', this.$URLS.cute));
        },
    },
};
</script>

<style scoped>
.profile-module-root {
    position: relative;
}
.content {
    position: relative;
}
#profile-info {
    padding: 5px 0;
    display: grid;
    grid-template-columns: 40px auto;
    grid-column-gap: 5px;
    grid-template-rows: 20px 20px;
    grid-template-areas:
        'img name'
        'img email';
}
#profile-info > * {
    margin: 0;
    padding: 0;
}

#spotlight-overview {
    display: grid;
    align-items: center;
    grid-template-columns: 1fr 1fr;
    grid-template-rows: 50px 20px auto;
}

.spotlight-action {
    display: flex;
    flex-direction: row;
    border: none;
    border-top: 1px solid #e5e5e5;
    color: #8e67c3;
}
.spotlight-action:hover {
    border-top: 1px solid #e5e5e5;
    background-color: rgba(100, 100, 100, 0.1);
}

#spotlight-actions {
    margin-bottom: 1px;
}
</style>

<style>
#progress-secondary > *:first-child {
    background-color: var(--secondary) !important;
}
</style>
